import { isNumber, isObject, isString } from "lodash-es";
import { Upload, DetailedError } from "tus-js-client";
import { api } from "@/modules/api";
import { clientEnvModule } from "@/modules/client-env";
import { logger } from "@/modules/logger";
import { uuidModule } from "@/modules/uuid";
import { GLOBAL_API_CLIENT } from "@/modules/api/globalApiClient";
import { getNetworkInfo } from "@/domains/network/status";
import { getFileExtensionFromMimeType, normalizeMimeType } from "./mime-utils";
import type { TranscriptArrayItem } from "@/modules/audio-recording/legacy";
import { truncate } from "@/modules/truncate/truncate";

const TUS_ENDPOINT = "https://storage-api.mem.ai/files";
const TUS_CHUNK_SIZE = 10 * 1024 * 1024; // 10MB
const FINALIZE_UPLOAD_TIMEOUT_MS = 60_000;
const FINALIZE_PROGRESS_START = 90;
const FINALIZE_PROGRESS_MAX = 99;
const FINALIZE_PROGRESS_DURATION_MS = 10_000;
const FINALIZE_PROGRESS_INTERVAL_MS = 500;
const TUS_PROGRESS_SCALE_BASE = 100;
const TUS_FINGERPRINT_PREFIX = "mem-audio";
const TUS_FINGERPRINT_SEPARATOR = "::";

enum TranscriptProvider {
  AssemblyAiStreaming = "ASSEMBLY_AI_STREAMING",
  AppleSpeech = "APPLE_SPEECH",
}

const DEFAULT_TRANSCRIPT_PROVIDER: TranscriptProvider = TranscriptProvider.AssemblyAiStreaming;

function buildAssociatedTranscriptData(params: {
  transcriptText?: string;
  transcriptArray?: TranscriptArrayItem[];
  transcriptProvider?: TranscriptProvider | null;
}): {
  transcript_text: string | null;
  transcript_array: TranscriptArrayItem[] | null;
  transcript_provider: TranscriptProvider | null;
} | null {
  const transcriptText = params.transcriptText?.trim() || null;
  const transcriptArray = params.transcriptArray?.length ? params.transcriptArray : null;

  if (!transcriptText && !transcriptArray) return null;

  return {
    transcript_text: transcriptText,
    transcript_array: transcriptArray,
    transcript_provider: params.transcriptProvider ?? DEFAULT_TRANSCRIPT_PROVIDER,
  };
}

export interface UploadMeta {
  capturedAudioId: string;
  associatedAudioRecordingId: string;
  transcriptText: string;
  transcriptArray: TranscriptArrayItem[];
  preferredMimeType?: string;
}

export type UploadResult = { success: true } | { success: false; error: string };

interface ProgressReporterConfig {
  onProgress: ((percent: number, loaded: number, total: number) => void) | undefined;
  totalBytes: number;
  audioRecordingId: string;
  filename: string;
}

interface FinalizeProgressRampConfig {
  onProgress: (percent: number) => void;
  startPercent: number;
  maxPercent: number;
  durationMs: number;
  intervalMs: number;
  logContext: { audioRecordingId: string; filename: string };
}

export class UploadPauser {
  private paused = false;
  private waiters: Array<() => void> = [];

  pause(): void {
    this.paused = true;
  }

  resume(): void {
    if (!this.paused) return;
    this.paused = false;
    this.waiters.splice(0).forEach(resolve => resolve());
  }

  isPaused(): boolean {
    return this.paused;
  }

  async waitIfPaused(): Promise<void> {
    if (!this.paused) return;
    await new Promise<void>(resolve => this.waiters.push(resolve));
  }
}

export async function uploadRecording(
  blob: Blob | null,
  meta: UploadMeta,
  opts?: {
    onProgress?: (percent: number, loaded: number, total: number) => void;
    signal?: AbortSignal;
    pauser?: UploadPauser;
  }
): Promise<UploadResult> {
  const uploadStartTime = Date.now();
  const uploadAttemptId = uuidModule.generate();
  const mimeType = normalizeMimeType({ mimeTypeStr: blob?.type || meta.preferredMimeType });

  if (!blob) return { success: false, error: "No audio recording available to upload" };
  if (!mimeType) return { success: false, error: "No supported MIME type found" };

  const fileExtension = getFileExtensionFromMimeType({ mimeTypeStr: mimeType });
  const filename = `${meta.associatedAudioRecordingId}.${fileExtension}`;
  const progressReporter = createUploadProgressReporter({
    onProgress: opts?.onProgress,
    totalBytes: blob.size,
    audioRecordingId: meta.associatedAudioRecordingId,
    filename,
  });

  try {
    logger.info({
      message: "[tus-upload-service] upload start",
      info: {
        uploadAttemptId,
        audioRecordingId: meta.associatedAudioRecordingId,
        filename,
        mimeType,
        sizeBytes: blob.size,
        clientVersion: clientEnvModule.packageVersion(),
      },
    });

    // Register upload
    const registerStartTime = Date.now();
    const registerResult = await registerUpload({
      uploadAttemptId,
      audioRecordingId: meta.associatedAudioRecordingId,
      filename,
      mimeType,
      transcriptText: meta.transcriptText,
      transcriptArray: meta.transcriptArray,
    });

    if (!registerResult) {
      return { success: false, error: "Failed to register upload" };
    }

    logger.info({
      message: "[tus-upload-service] registerUpload complete",
      info: {
        uploadAttemptId,
        audioRecordingId: meta.associatedAudioRecordingId,
        normalizedFilename: registerResult.normalized_filename,
        bucketName: registerResult.bucket_name,
        spaceAccountId: registerResult.space_account_id,
        durationMs: Date.now() - registerStartTime,
      },
    });

    // Upload via TUS
    const tusStartTime = Date.now();
    const tusResult = await uploadViaTus({
      uploadAttemptId,
      file: blob,
      mimeType,
      filename: registerResult.normalized_filename,
      spaceAccountId: registerResult.space_account_id,
      audioRecordingId: meta.associatedAudioRecordingId,
      bucketName: registerResult.bucket_name,
      onProgress: progressReporter.reportTusProgress,
      signal: opts?.signal,
    });

    if (!tusResult.success) {
      return { success: false, error: tusResult.error ?? "TUS upload failed" };
    }

    logger.info({
      message: "[tus-upload-service] tus upload completed",
      info: {
        uploadAttemptId,
        audioRecordingId: meta.associatedAudioRecordingId,
        filename: registerResult.normalized_filename,
        durationMs: Date.now() - tusStartTime,
      },
    });

    // Finalize upload
    const finalizeStartTime = Date.now();
    logger.info({
      message: "[tus-upload-service] finalizeUpload start",
      info: {
        uploadAttemptId,
        audioRecordingId: meta.associatedAudioRecordingId,
        filename: registerResult.normalized_filename,
      },
    });
    progressReporter.startFinalizeProgress();
    let finalizeResult = false;
    try {
      finalizeResult = await finalizeUpload({
        uploadAttemptId,
        audioRecordingId: meta.associatedAudioRecordingId,
        filename: registerResult.normalized_filename,
        mimeType,
        transcriptText: meta.transcriptText,
        transcriptArray: meta.transcriptArray,
      });
    } finally {
      progressReporter.stopFinalizeProgress();
    }

    if (!finalizeResult) {
      return { success: false, error: "Failed to finalize upload" };
    }

    progressReporter.complete();
    logger.info({
      message: "[tus-upload-service] finalizeUpload complete",
      info: {
        uploadAttemptId,
        audioRecordingId: meta.associatedAudioRecordingId,
        filename: registerResult.normalized_filename,
        durationMs: Date.now() - finalizeStartTime,
        totalDurationMs: Date.now() - uploadStartTime,
      },
    });

    return { success: true };
  } catch (error: unknown) {
    progressReporter.stopFinalizeProgress();
    const errorMessage = error instanceof Error ? error.message : "Upload failed";
    logger.error({
      message: "[tus-upload-service] Upload failed",
      info: {
        uploadAttemptId,
        error: errorMessage,
        audioRecordingId: meta.associatedAudioRecordingId,
      },
    });
    return { success: false, error: errorMessage };
  }
}

function createUploadProgressReporter({
  onProgress,
  totalBytes,
  audioRecordingId,
  filename,
}: ProgressReporterConfig) {
  let lastReported = 0;
  const finalizeRamp = createFinalizeProgressRamp({
    onProgress: (percent: number) => report(percent),
    startPercent: FINALIZE_PROGRESS_START,
    maxPercent: FINALIZE_PROGRESS_MAX,
    durationMs: FINALIZE_PROGRESS_DURATION_MS,
    intervalMs: FINALIZE_PROGRESS_INTERVAL_MS,
    logContext: { audioRecordingId, filename },
  });

  function report(percent: number, loaded = totalBytes, total = totalBytes) {
    if (!onProgress) return;
    const clamped = Math.max(lastReported, Math.min(100, Math.round(percent)));
    if (clamped === lastReported) return;
    lastReported = clamped;
    onProgress(clamped, loaded, total);
  }

  const reportTusProgress = (percent: number, loaded: number, total: number) => {
    // Scale tus progress (0-100) to 0-FINALIZE_PROGRESS_START (e.g., 0-90%)
    const scaled = Math.min(
      FINALIZE_PROGRESS_START,
      Math.floor(percent * (FINALIZE_PROGRESS_START / TUS_PROGRESS_SCALE_BASE))
    );
    report(scaled, loaded, total);
  };

  const startFinalizeProgress = () => {
    finalizeRamp.start();
  };

  const stopFinalizeProgress = () => {
    finalizeRamp.stop();
  };

  const complete = () => {
    finalizeRamp.stop();
    report(100);
  };

  return { reportTusProgress, startFinalizeProgress, stopFinalizeProgress, complete };
}

function createFinalizeProgressRamp({
  onProgress,
  startPercent,
  maxPercent,
  durationMs,
  intervalMs,
  logContext,
}: FinalizeProgressRampConfig) {
  let timer: ReturnType<typeof setInterval> | null = null;
  let startedAt: number | null = null;

  const tick = () => {
    if (startedAt === null) return;
    const elapsed = Date.now() - startedAt;
    const ratio = Math.min(elapsed / durationMs, 1);
    const target = startPercent + Math.floor(ratio * (maxPercent - startPercent));
    onProgress(target);
    if (ratio >= 1 && timer) {
      clearInterval(timer);
      timer = null;
    }
  };

  const start = () => {
    if (timer) return;
    startedAt = Date.now();
    logger.info({
      message: "[tus-upload-service] finalize progress ramp start",
      info: { ...logContext },
    });
    timer = setInterval(tick, intervalMs);
    tick();
  };

  const stop = () => {
    if (timer) {
      clearInterval(timer);
      timer = null;
      logger.info({
        message: "[tus-upload-service] finalize progress ramp stop",
        info: { ...logContext },
      });
    }
    startedAt = null;
  };

  return { start, stop };
}

interface RegisterUploadResponse {
  bucket_name: string;
  space_account_id: string;
  audio_recording_id: string;
  normalized_filename: string;
}

async function registerUpload(params: {
  uploadAttemptId?: string;
  audioRecordingId: string;
  filename: string;
  mimeType: string;
  transcriptText?: string;
  transcriptArray?: TranscriptArrayItem[];
}): Promise<RegisterUploadResponse | null> {
  try {
    const response = await api.post("/v2/audio-recordings/{audio_recording_id}/register-upload", {
      params: { path: { audio_recording_id: params.audioRecordingId } },
      body: {
        filename: params.filename,
        mime_type: params.mimeType,
        associated_transcript_data: buildAssociatedTranscriptData({
          transcriptText: params.transcriptText,
          transcriptArray: params.transcriptArray,
        }),
      },
    });
    return response.data ?? null;
  } catch (error) {
    logger.error({
      message: "[tus-upload-service] registerUpload failed",
      info: {
        uploadAttemptId: params.uploadAttemptId ?? null,
        audioRecordingId: params.audioRecordingId,
        error: String(error),
      },
    });
    return null;
  }
}

async function finalizeUpload(params: {
  uploadAttemptId?: string;
  audioRecordingId: string;
  filename: string;
  mimeType: string;
  transcriptText?: string;
  transcriptArray?: TranscriptArrayItem[];
}): Promise<boolean> {
  try {
    const response = await withTimeout(
      api.post("/v2/audio-recordings/{audio_recording_id}/finalize-upload", {
        params: { path: { audio_recording_id: params.audioRecordingId } },
        body: {
          filename: params.filename,
          mime_type: params.mimeType,
          associated_transcript_data: buildAssociatedTranscriptData({
            transcriptText: params.transcriptText,
            transcriptArray: params.transcriptArray,
          }),
        },
      }),
      FINALIZE_UPLOAD_TIMEOUT_MS,
      "Finalize upload timed out"
    );
    return response.data?.success ?? false;
  } catch (error) {
    const errorMessage = error instanceof Error ? error.message : String(error);
    logger.error({
      message: "[tus-upload-service] finalizeUpload failed",
      info: {
        uploadAttemptId: params.uploadAttemptId ?? null,
        audioRecordingId: params.audioRecordingId,
        error: errorMessage,
      },
    });
    return false;
  }
}

async function uploadViaTus(params: {
  uploadAttemptId: string;
  file: Blob;
  mimeType: string;
  filename: string;
  spaceAccountId: string;
  audioRecordingId: string;
  bucketName: string;
  onProgress?: (percent: number, loaded: number, total: number) => void;
  signal?: AbortSignal;
}): Promise<{ success: boolean; error?: string }> {
  const authToken = GLOBAL_API_CLIENT.getAuthToken();
  if (!authToken) {
    return { success: false, error: "No auth token available" };
  }

  return new Promise(resolve => {
    const tusStartTime = Date.now();
    let loggedUrl = false;
    let previousUploadsCount = 0;
    let previousUploadPath: string | null = null;
    const networkInfo = getNetworkInfo();
    const clientVersion = clientEnvModule.packageVersion();
    const headers: Record<string, string> = {
      Authorization: `OAuthAccessToken ${authToken}`,
      "X-Mem-Upload-Attempt": params.uploadAttemptId,
    };
    if (clientVersion) {
      headers["X-Mem-Client-Version"] = clientVersion;
    }
    const upload = new Upload(params.file, {
      endpoint: TUS_ENDPOINT,
      uploadSize: params.file.size,
      chunkSize: TUS_CHUNK_SIZE,
      retryDelays: [0, 1000, 3000, 7000, 15000],
      removeFingerprintOnSuccess: true,
      fingerprint: async (file, options) => {
        return generateTusFingerprint({
          audioRecordingId: params.audioRecordingId,
          filename: params.filename,
          fileSize: typeof file.size === "number" ? file.size : "unknown",
          endpoint: options.endpoint ?? "unknown",
        });
      },
      metadata: {
        filename: params.filename,
        filetype: params.mimeType,
        space_account_id: params.spaceAccountId,
        file_id: params.audioRecordingId,
        bucket_name: params.bucketName,
        is_chunk: "false",
        content_length: String(params.file.size),
      },
      headers,
      onUploadUrlAvailable: () => {
        if (loggedUrl) return;
        loggedUrl = true;
        const uploadPath = safePathFromUrl({ url: upload.url });
        logger.info({
          message: "[tus-upload-service] tus upload url available",
          info: {
            uploadAttemptId: params.uploadAttemptId ?? null,
            audioRecordingId: params.audioRecordingId,
            uploadPath: uploadPath ?? null,
            previousUploadsCount,
            previousUploadPath,
          },
        });
      },
      onProgress: (bytesSent: number, bytesTotal: number) => {
        if (params.onProgress && bytesTotal > 0) {
          params.onProgress(Math.floor((bytesSent / bytesTotal) * 100), bytesSent, bytesTotal);
        }
      },
      onSuccess: () => {
        logger.info({
          message: "[tus-upload-service] tus upload success",
          info: {
            uploadAttemptId: params.uploadAttemptId,
            audioRecordingId: params.audioRecordingId,
            filename: params.filename,
            durationMs: Date.now() - tusStartTime,
            previousUploadsCount,
            previousUploadPath,
          },
        });
        resolve({ success: true });
      },
      onError: (error: Error | DetailedError) => {
        const message = error.message ?? "TUS upload failed";
        const requestInfo = extractTusRequestInfo({ error });
        logger.error({
          message: "[tus-upload-service] tus upload error",
          info: {
            uploadAttemptId: params.uploadAttemptId,
            audioRecordingId: params.audioRecordingId,
            filename: params.filename,
            error: message,
            durationMs: Date.now() - tusStartTime,
            previousUploadsCount,
            previousUploadPath,
            requestInfo,
            networkInfo,
          },
        });
        resolve({ success: false, error: message });
      },
    });

    if (params.signal) {
      if (params.signal.aborted) {
        resolve({ success: false, error: "Upload aborted" });
        return;
      }
      params.signal.addEventListener("abort", () => {
        upload.abort(true);
        resolve({ success: false, error: "Upload aborted" });
      });
    }

    void (async () => {
      try {
        const previousUploads = await upload.findPreviousUploads();
        previousUploadsCount = previousUploads.length;
        if (previousUploads.length > 0) {
          const safePath = safePathFromUrl({ url: previousUploads[0]?.uploadUrl });
          if (safePath) {
            previousUploadPath = safePath;
          } else {
            previousUploadPath = null;
          }
        } else {
          previousUploadPath = null;
        }
        logger.info({
          message: "[tus-upload-service] tus resume candidates",
          info: {
            uploadAttemptId: params.uploadAttemptId,
            audioRecordingId: params.audioRecordingId,
            previousUploadsCount,
            previousUploadPath,
          },
        });
      } catch (error) {
        logger.warn({
          message: "[tus-upload-service] tus resume lookup failed",
          info: {
            uploadAttemptId: params.uploadAttemptId,
            audioRecordingId: params.audioRecordingId,
            error: error instanceof Error ? error.message : String(error),
          },
        });
      }
      upload.start();
    })();
  });
}

function withTimeout<T>(promise: Promise<T>, timeoutMs: number, message: string): Promise<T> {
  if (!timeoutMs) return promise;

  let timeoutHandle: ReturnType<typeof setTimeout> | null = null;

  const timeoutPromise = new Promise<T>((_, reject) => {
    timeoutHandle = setTimeout(() => reject(new Error(message)), timeoutMs);
  });

  return Promise.race([promise, timeoutPromise]).finally(() => {
    if (timeoutHandle) {
      clearTimeout(timeoutHandle);
    }
  });
}

/**
 * Generates a unique fingerprint for TUS upload resumption.
 * The fingerprint identifies an upload by its recording ID, filename, size, and endpoint.
 */
export function generateTusFingerprint(params: {
  audioRecordingId: string;
  filename: string;
  fileSize: number | "unknown";
  endpoint: string;
}): string {
  return [
    TUS_FINGERPRINT_PREFIX,
    params.audioRecordingId,
    params.filename,
    params.fileSize,
    params.endpoint,
  ].join(TUS_FINGERPRINT_SEPARATOR);
}

function safePathFromUrl({ url }: { url: unknown }): string | undefined {
  if (!isString(url)) {
    return undefined;
  }
  if (url.length === 0) {
    return undefined;
  }
  try {
    return new URL(url).pathname;
  } catch {
    return url;
  }
}

type TusRequestInfo = {
  method?: string;
  urlPath?: string;
  status?: number;
  statusText?: string;
  readyState?: number;
  responseText?: string;
  causingErrorType?: string;
  causingErrorMessage?: string;
  progressEventType?: string;
  progressEventLoaded?: number;
  progressEventTotal?: number;
  errorKeys?: string[];
};

type TusRequestLike = {
  method?: string;
  url?: string;
  status?: number;
  statusText?: string;
  readyState?: number;
  responseText?: string;
};

function extractTusRequestInfo({ error }: { error: unknown }): TusRequestInfo | null {
  if (!isObject(error)) {
    return null;
  }

  const info: TusRequestInfo = {};
  info.errorKeys = Object.keys(error);

  const request = getTusRequest({ error });
  if (request) {
    info.method = request.method;
    info.urlPath = safePathFromUrl({ url: request.url });
    info.status = request.status;
    info.statusText = request.statusText;
    info.readyState = request.readyState;
    info.responseText = buildTruncatedText({ value: request.responseText, max: 200 });
  }

  const causingError = extractCausingError({ error });
  if (causingError) {
    info.causingErrorType = causingError.constructor?.name ?? typeof causingError;
    if (causingError instanceof Error) {
      info.causingErrorMessage = causingError.message;
    }
    if (typeof ProgressEvent !== "undefined" && causingError instanceof ProgressEvent) {
      info.progressEventType = causingError.type;
      info.progressEventLoaded = causingError.loaded;
      info.progressEventTotal = causingError.total;
    }
  }

  return info;
}

function extractCausingError({ error }: { error: unknown }): unknown {
  if (!isObject(error)) {
    return null;
  }
  const candidate = error as {
    causingError?: unknown;
    originalError?: unknown;
    cause?: unknown;
  };
  return candidate.causingError ?? candidate.originalError ?? candidate.cause ?? null;
}

function getTusRequest({ error }: { error: unknown }): TusRequestLike | null {
  if (!isObject(error)) {
    return null;
  }
  const candidate = error as {
    originalRequest?: TusRequestLike;
    request?: TusRequestLike;
  };
  if (candidate.originalRequest) {
    return candidate.originalRequest;
  }
  if (candidate.request) {
    return candidate.request;
  }
  return null;
}

function buildTruncatedText({ value, max }: { value: unknown; max: number }): string | undefined {
  if (!isString(value)) {
    return undefined;
  }
  if (!isNumber(max) || max <= 0) {
    return undefined;
  }
  return truncate(value, max);
}
